import * as Sentry from '@sentry/react';

function readRuntimeConfig(key) {
  if (typeof window !== 'undefined' && window.APP_CONFIG?.[key] != null) {
    const value = String(window.APP_CONFIG[key]).trim();
    return value !== '' ? value : undefined;
  }
  return undefined;
}

/** @returns {string|undefined} */
export function getSentryDsn() {
  return readRuntimeConfig('SENTRY_DSN') || import.meta.env.VITE_SENTRY_DSN || undefined;
}

export function isSentryEnabled() {
  return Boolean(getSentryDsn());
}

/** Sentry environment tag (staging / production / development). */
export function getSentryEnvironment() {
  return (
    readRuntimeConfig('SENTRY_ENVIRONMENT') ||
    import.meta.env.VITE_SENTRY_ENVIRONMENT ||
    import.meta.env.MODE ||
    'development'
  );
}

/** Staging + production use lower sampling; local dev stays higher. */
export function getSentrySampleRate() {
  const env = getSentryEnvironment();
  const override = readRuntimeConfig('SENTRY_TRACES_SAMPLE_RATE');
  if (override != null) {
    const n = Number(override);
    if (Number.isFinite(n) && n >= 0) return Math.min(1, n);
  }
  if (env === 'production' || env === 'staging') return 0.02;
  return 0.2;
}

function feedbackEnabled() {
  const runtime = readRuntimeConfig('SENTRY_FEEDBACK_ENABLED');
  if (runtime === '0' || runtime === 'false') return false;
  if (import.meta.env.VITE_SENTRY_FEEDBACK_ENABLED === 'false') return false;
  return true;
}

function replayEnabled() {
  const runtime = readRuntimeConfig('SENTRY_REPLAY_ENABLED');
  if (runtime === '0' || runtime === 'false') return false;
  if (import.meta.env.VITE_SENTRY_REPLAY_ENABLED === 'false') return false;
  return true;
}

/** @type {ReturnType<typeof Sentry.feedbackIntegration> | null} */
let sentryFeedbackIntegration = null;

function createFeedbackIntegration() {
  return Sentry.feedbackIntegration({
    autoInject: false,
    colorScheme: 'system',
    showBranding: true,
    showName: true,
    showEmail: true,
    isNameRequired: false,
    isEmailRequired: false,
    enableScreenshot: true,
    useSentryUser: {
      email: 'email',
      name: 'username',
    },
    formTitle: 'Report an issue',
    submitButtonLabel: 'Send report',
    cancelButtonLabel: 'Cancel',
    messageLabel: 'What happened?',
    messagePlaceholder:
      'Describe the problem, what you expected, and any steps to reproduce.',
    successMessageText: 'Thank you — your report was sent.',
    addScreenshotButtonLabel: 'Add screenshot',
    removeScreenshotButtonLabel: 'Remove screenshot',
    tags: {
      app: 'zra-frontend',
    },
  });
}

export function isSentryFeedbackAvailable() {
  return sentryFeedbackIntegration != null;
}

const SENTRY_FEEDBACK_HOST_ID = 'sentry-feedback';

/** Anchor the feedback panel near a trigger element (e.g. sidebar button). */
function applyFeedbackDialogPosition(anchorEl) {
  const host = document.getElementById(SENTRY_FEEDBACK_HOST_ID);
  if (!host) return;

  if (!anchorEl) {
    host.style.removeProperty('--inset');
    return;
  }

  const rect = anchorEl.getBoundingClientRect();
  const gap = 12;
  const bottom = Math.max(16, window.innerHeight - rect.top + gap);
  const left = Math.max(16, rect.left);

  host.style.setProperty('--inset', `auto auto ${bottom}px ${left}px`);
}

/** Open the Sentry user-feedback dialog (optionally anchored to a trigger element). */
export async function openSentryFeedback(anchorEl) {
  if (!sentryFeedbackIntegration?.createForm) return;
  try {
    const dialog = await sentryFeedbackIntegration.createForm();
    applyFeedbackDialogPosition(anchorEl);
    dialog.appendToDom();
    dialog.open();
  } catch (err) {
    console.warn('[Sentry] Failed to open feedback dialog', err);
  }
}

/** @returns {import('@sentry/react').Integration[]} */
function buildIntegrations() {
  const integrations = [Sentry.browserTracingIntegration()];

  if (replayEnabled()) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllInputs: true,
        blockAllMedia: false,
        networkDetailAllowUrls: [window.location.origin, '/api'],
      })
    );
  }

  if (feedbackEnabled()) {
    sentryFeedbackIntegration = createFeedbackIntegration();
    integrations.push(sentryFeedbackIntegration);
  }

  return integrations;
}

/** Initialize Sentry when a DSN is configured. Returns whether init ran. */
export function initSentry() {
  const dsn = getSentryDsn();
  if (!dsn) return false;

  const sampleRate = getSentrySampleRate();
  const replayActive = replayEnabled();

  Sentry.init({
    dsn,
    environment: getSentryEnvironment(),
    integrations: buildIntegrations(),
    tracesSampleRate: sampleRate,
    sampleRate: 0.5,
    ...(replayActive
      ? {
          replaysSessionSampleRate: sampleRate,
          replaysOnErrorSampleRate: sampleRate,
        }
      : {}),
    debug: false,
  });

  return true;
}

export { Sentry };
